'use client'

import { useState } from 'react'

/**
 * "How projects run" — the mock's tablist: three stage tabs on the left,
 * the selected stage's panel on the right. Client-side only because of the
 * tab state; everything else on /about stays a Server Component.
 *
 * Keyboard contract matches the mock's script: arrow keys move between
 * tabs and activate as they go (automatic activation), with roving
 * tabindex so the group is a single tab stop.
 */
const STAGES = [
  {
    id: 'scope',
    number: '01',
    tab: 'Scope and map',
    span: '1 to 2 weeks',
    title: 'Scope and map.',
    copy: 'Align on the goal, map the system, and define the result that matters, whether the work is a product, a brand system, AI visibility, or qualified pipeline.',
    deliverables: [
      'Goal and stakeholder alignment',
      'Scope, standards, and guardrails',
      'Success metrics and delivery plan',
    ],
    time: ['1–2', 'weeks'],
    outcome:
      'Outcome: one scoped plan everyone has agreed to before the build starts.',
  },
  {
    id: 'build',
    number: '02',
    tab: 'Build and ship',
    span: '6 to 10 weeks',
    title: 'Build and ship.',
    copy: 'Design and develop the production product, brand system, visibility infrastructure, or growth engine in short, reviewable checkpoints.',
    deliverables: [
      'Production-ready application or system',
      'Checkpoint demos and acceptance reviews',
      'Code, design assets, and documentation',
    ],
    time: ['6–10', 'weeks'],
    outcome:
      'Outcome: launch-ready work built to scope, with the code and assets you own.',
  },
  {
    id: 'launch',
    number: '03',
    tab: 'Launch and hand off',
    span: '2 to 4 weeks',
    title: 'Launch and hand off.',
    copy: 'Release the work, prepare your team to operate it, and transfer everything needed to keep improving without depending on us.',
    deliverables: [
      'Launch support and production verification',
      'Operational documentation and training',
      'Full code, asset, and system handoff',
    ],
    time: ['2–4', 'weeks'],
    outcome:
      'Outcome: live work your team owns and can run, with optional ongoing support.',
  },
]

export default function ProcessStages() {
  const [active, setActive] = useState(0)

  const onKeyDown = (e: React.KeyboardEvent) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
    if (!keys.includes(e.key)) return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1
    const next = (active + dir + STAGES.length) % STAGES.length
    setActive(next)
    document.getElementById(`about-tab-${STAGES[next].id}`)?.focus()
  }

  const stage = STAGES[active]

  return (
    <div className='grid lg:grid-cols-[21rem_1fr] border border-line-strong bg-card'>
      <div
        role='tablist'
        aria-label='Project stages'
        aria-orientation='vertical'
        className='border-b lg:border-b-0 lg:border-r border-line'
      >
        {STAGES.map((s, i) => {
          const selected = i === active
          return (
            <button
              key={s.id}
              id={`about-tab-${s.id}`}
              type='button'
              role='tab'
              aria-selected={selected}
              aria-controls={`about-panel-${s.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(i)}
              onKeyDown={onKeyDown}
              className={`w-full grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 p-6 text-left border-b border-line last:border-b-0 transition-colors ${
                selected ? 'bg-muted/60' : 'hover:bg-muted/30'
              }`}
            >
              <span
                className={`font-mono text-xs ${
                  selected ? 'text-cta' : 'text-muted-foreground'
                }`}
              >
                {s.number}
              </span>
              <span>
                <span className='block text-sm font-semibold text-foreground'>
                  {s.tab}
                </span>
                <span className='block text-xs text-muted-foreground mt-0.5'>
                  {s.span}
                </span>
              </span>
              <span
                aria-hidden='true'
                className={selected ? 'text-cta' : 'text-muted-foreground'}
              >
                →
              </span>
            </button>
          )
        })}
      </div>

      <article
        id={`about-panel-${stage.id}`}
        role='tabpanel'
        aria-labelledby={`about-tab-${stage.id}`}
        className='grid md:grid-cols-[1.4fr_1fr]'
      >
        <div className='p-7 md:p-10'>
          <p className='instrument-label m-0 mb-4'>Stage {stage.number}</p>
          <h3 className='card-headline card-headline--3xl'>{stage.title}</h3>
          <p className='text-muted-foreground leading-relaxed max-w-lg m-0 mt-4'>
            {stage.copy}
          </p>
          <ul className='mt-7 space-y-2.5'>
            {stage.deliverables.map(d => (
              <li
                key={d}
                className='flex gap-2.5 text-sm text-muted-foreground m-0'
              >
                <span
                  className='text-cta font-bold shrink-0'
                  aria-hidden='true'
                >
                  ✓
                </span>
                {d}
              </li>
            ))}
          </ul>
        </div>
        <aside className='p-7 md:p-10 border-t md:border-t-0 md:border-l border-line flex flex-col justify-center'>
          <p className='instrument-label text-muted-foreground m-0 mb-3'>
            Timeframe
          </p>
          <div className='font-display font-bold text-5xl leading-[0.9] tracking-[-0.05em] text-foreground'>
            {stage.time[0]}
            <br />
            {stage.time[1]}
          </div>
          <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-5'>
            {stage.outcome}
          </p>
        </aside>
      </article>
    </div>
  )
}
